<template>
  <div class="ttd-digital-list">
    <div class="page-header">
      <h2 class="page-title">Permintaan Tanda Tangan Digital</h2>
    </div>

    <div class="actions-bar">
      <a-row :gutter="[16, 16]">
        <a-col :xs="24" :sm="24" :md="24" :lg="24">
          <a-input
            v-model:value="searchText"
            placeholder="Cari nomor surat atau jenis surat"
            @input="handleSearch"
          >
            <template #prefix>
              <SearchOutlined style="color: rgba(0, 0, 0, 0.25); margin-right: 4px" />
            </template>
          </a-input>
        </a-col>
      </a-row>
    </div>

    <a-table
      :columns="columns"
      :data-source="filteredList"
      :loading="loading"
      :pagination="{
        current: pagination.current,
        pageSize: pagination.pageSize,
        total: pagination.total,
        showSizeChanger: true,
        pageSizeOptions: pagination.pageSizeOptions,
        showTotal: (total, range) => `Total ${total} baris`,
      }"
      :scroll="{ x: 1200 }"
      :custom-row="customRow"
      @change="handleTableChange"
      class="verifikasi-table"
      row-key="id"
    >
      <template #nomorSurat="{ text }">
        <span class="surat-link">{{ text }}</span>
      </template>

      <template #tanggalSurat="{ text }">
        {{ formatDate(text) }}
      </template>

      <template #createdAt="{ text }">
        {{ formatDateTime(text) }}
      </template>

      <template #status="{ text }">
        <a-tag :color="getStatusColor(text)">
          <strong>{{ text }}</strong>
        </a-tag>
      </template>
    </a-table>
  </div>
</template>

<script>
import { defineComponent, ref, onMounted, onBeforeUnmount, reactive, computed, h } from 'vue'
import { useRouter } from 'vue-router'
import { notification, DatePicker } from 'ant-design-vue'
import { SearchOutlined } from '@ant-design/icons-vue'
import { penandatangananAPI } from '@/services/axios/perki'
import moment from 'moment'
import 'moment/locale/id'

moment.locale('id')

export default defineComponent({
  name: 'TtdDigitalList',
  components: {
    SearchOutlined,
  },
  setup() {
    const router = useRouter()
    const loading = ref(false)
    const suratList = ref([])
    const searchText = ref('')
    const searchTimer = ref(null)

    const state = reactive({
      filteredInfo: null,
      sortedInfo: null,
    })

    const pagination = reactive({
      current: 1,
      pageSize: 10,
      showSizeChanger: true,
      pageSizeOptions: ['10', '20', '50', '100'],
      total: 0,
    })

    // Table columns
    const columns = [
      {
        title: 'No. Surat',
        dataIndex: 'nomorSurat',
        key: 'nomorSurat',
        width: 180,
        // sorter: (a, b) => (a.nomorSurat || '').localeCompare(b.nomorSurat || ''),
        slots: { customRender: 'nomorSurat' },
      },
      {
        title: 'Tanggal Surat',
        dataIndex: 'tanggalSurat',
        key: 'tanggalSurat',
        width: 150,
        sorter: (a, b) => new Date(a.tanggalSurat) - new Date(b.tanggalSurat),
        slots: { customRender: 'tanggalSurat' },
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) =>
          h('div', { style: { padding: '8px' } }, [
            h(DatePicker.RangePicker, {
              style: { marginBottom: '8px', display: 'block' },
              value: selectedKeys[0],
              onChange: e => setSelectedKeys(e ? [e] : []),
            }),
            h('div', { style: { display: 'flex', justifyContent: 'space-between' } }, [
              h(
                'a',
                {
                  style: { fontSize: '12px' },
                  onClick: () => {
                    clearFilters()
                  },
                },
                'Reset',
              ),
              h(
                'a',
                {
                  style: { fontSize: '12px' },
                  onClick: () => {
                    confirm()
                  },
                },
                'OK',
              ),
            ]),
          ]),
        onFilter: (value, record) => {
          if (!value || !Array.isArray(value) || value.length < 2 || !record.tanggalSurat) {
            return true
          }
          const recordDate = moment(record.tanggalSurat)
          const startDate = moment(value[0])
          const endDate = moment(value[1])
          return recordDate.isBetween(startDate, endDate, 'day', '[]')
        },
      },
      {
        title: 'Jenis Surat',
        dataIndex: 'jenisSurat',
        key: 'jenisSurat',
        width: 200,
        filters: [
          { text: 'Surat Tugas', value: 'Surat Tugas' },
          { text: 'Cabang', value: 'Cabang' },
          { text: 'Farmasi', value: 'Farmasi' },
          { text: 'Instansi Pemerintah', value: 'Instansi Pemerintah' },
          { text: 'Internal - Surat', value: 'Internal - Surat' },
          { text: 'Internal - Undangan', value: 'Internal - Undangan' },
          { text: 'Kepegawaian PP', value: 'Kepegawaian PP' },
          { text: 'MoU', value: 'MoU' },
          { text: 'PKS', value: 'PKS' },
          { text: 'OPKeseminatan Lain', value: 'OPKeseminatan Lain' },
          { text: 'PB IDI', value: 'PB IDI' },
          { text: 'Perusahaan Swasta', value: 'Perusahaan Swasta' },
          { text: 'Podcast', value: 'Podcast' },
          { text: 'Pokja', value: 'Pokja' },
          { text: 'Rumah Sakit', value: 'Rumah Sakit' },
          { text: 'SK', value: 'SK' },
          { text: 'YJI', value: 'YJI' },
          { text: 'Lainnya', value: 'Lainnya' },
        ],
        onFilter: (value, record) => record.jenisSurat === value,
      },
      {
        title: 'Waktu Dibuat/Unggah',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 200,
        sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
        defaultSortOrder: 'descend',
        slots: { customRender: 'createdAt' },
      },
      {
        title: 'Status Surat',
        dataIndex: 'statusPenandatanganan',
        key: 'statusPenandatanganan',
        width: 150,
        filters: [
          { text: 'Butuh TTD', value: 'Butuh TTD' },
          { text: 'Selesai', value: 'Selesai' },
          { text: 'Dibatalkan', value: 'Dibatalkan' },
          { text: 'Revisi', value: 'Revisi' },
        ],
        onFilter: (value, record) => record.statusPenandatanganan === value,
        slots: { customRender: 'status' },
      },
    ]

    // Data is fetched from server with filters/sorting/pagination applied
    const filteredList = computed(() => suratList.value || [])

    // Format date
    const formatDate = dateStr => {
      if (!dateStr) return '-'
      return moment(dateStr).format('D MMMM YYYY')
    }

    // Format datetime
    const formatDateTime = dateStr => {
      if (!dateStr) return '-'
      return moment(dateStr).format('dddd YYYY-MM-DD HH:mm')
    }

    // Get status color
    const getStatusColor = status => {
      const colors = {
        'Butuh TTD': 'red',
        Selesai: 'green',
        Dibatalkan: 'default',
        Revisi: 'orange',
        Menunggu: 'blue',
      }
      return colors[status] || 'default'
    }

    // Convert penandatanganan status to display label
    const getPenandatangananStatusLabel = status => {
      const labels = {
        pending: 'Butuh TTD',
        signed: 'Selesai',
        revised: 'Revisi',
        revision_requested: 'Revisi',
      }
      return labels[status] || status || '-'
    }

    // Fetch data (server-side filtering/sorting/pagination)
    const fetchData = async () => {
      loading.value = true
      try {
        const params = {
          page: pagination.current,
          pageSize: pagination.pageSize,
          // ensure only Soft Copy is requested (business rule)
          bentukSurat: 'Soft copy',
        }

        // Search q: nomorSurat or jenisSurat (perihal excluded)
        if (searchText.value && searchText.value.trim()) params.q = searchText.value.trim()

        // jenisSurat filter (pick first if multiple)
        if (state.filteredInfo?.jenisSurat && state.filteredInfo.jenisSurat.length > 0) {
          params.jenisSurat = state.filteredInfo.jenisSurat[0]
        }

        // status filter: CSV
        if (state.filteredInfo?.status && state.filteredInfo.status.length > 0) {
          params.status = state.filteredInfo.status.join(',')
        }

        // tanggal filter
        if (state.filteredInfo?.tanggalSurat && state.filteredInfo.tanggalSurat.length > 0) {
          const range = state.filteredInfo.tanggalSurat[0]
          if (Array.isArray(range) && range.length === 2) {
            params.dateFrom = range[0].format('DD-MM-YYYY')
            params.dateTo = range[1].format('DD-MM-YYYY')
          }
        }

        // Sorting
        let sortBy = 'createdAt'
        let sortOrder = 'DESC'
        if (state.sortedInfo && state.sortedInfo.field) {
          if (state.sortedInfo.field === 'tanggalSurat') sortBy = 'tanggalSurat'
          else sortBy = 'createdAt'
        }
        if (state.sortedInfo && state.sortedInfo.order) {
          if (state.sortedInfo.order === 'ascend') sortOrder = 'ASC'
          else if (state.sortedInfo.order === 'descend') sortOrder = 'DESC'
          else {
            loading.value = false
            notification.error({ message: 'Error', description: 'Invalid sortOrder' })
            return
          }
        }
        params.sortBy = sortBy
        params.sortOrder = sortOrder

        console.debug('penandatanganan.fetchData params:', params)

        const response = await penandatangananAPI.getSuratForPenandatanganan(params)

        // Support both legacy array response and paginated response with data + pagination
        if (response.data?.data) {
          const payload = response.data.data
          // If backend returns { suratForPenandatanganan: [...], pagination: {...} }
          const list = payload.suratForPenandatanganan || payload
          suratList.value = (list || []).map(item => ({
            id: item.penandatangan?.id || item.surat?.id,
            suratId: item.surat?.id,
            nomorSurat: item.surat?.nomorSurat,
            tanggalSurat: item.surat?.tanggalSurat,
            jenisSurat: item.surat?.jenisSurat,
            sifatSurat: item.surat?.sifatSurat,
            createdAt: item.surat?.createdAt,
            pengajuSurat: item.surat?.creator?.name,
            statusPenandatanganan: getPenandatangananStatusLabel(item.penandatangan?.status),
            ...item,
          }))

          const paginationPayload = payload.pagination || {}
          pagination.current = paginationPayload.page || pagination.current
          pagination.pageSize = paginationPayload.pageSize || pagination.pageSize
          pagination.total = paginationPayload.totalRecords || pagination.total || 0
        } else {
          suratList.value = []
          pagination.total = 0
        }
      } catch (error) {
        console.error('Error fetching penandatanganan list:', error)
        notification.error({ message: 'Gagal memuat data penandatanganan' })
        suratList.value = []
        pagination.total = 0
      } finally {
        loading.value = false
      }
    }

    // Handle table change (filters, sorter, pagination)
    const handleTableChange = (pag, filters, sorter) => {
      pagination.current = pag.current
      pagination.pageSize = pag.pageSize
      state.filteredInfo = filters
      state.sortedInfo = sorter
      fetchData()
    }

    const handleSearch = () => {
      pagination.current = 1
      if (searchTimer.value) clearTimeout(searchTimer.value)
      searchTimer.value = setTimeout(() => {
        fetchData()
      }, 300)
    }

    onBeforeUnmount(() => {
      if (searchTimer.value) clearTimeout(searchTimer.value)
    })

    const handleDateRangeChange = () => {
      pagination.current = 1
      // date range change triggers server fetch via table filter confirm
      fetchData()
    }

    const handleFilterChange = () => {
      pagination.current = 1
      fetchData()
    }

    // Navigate to detail - uses suratId for routing
    const handleDetail = suratId => {
      router.push(`/ttd-digital/${suratId}`)
    }

    // Custom row - make entire row clickable
    const customRow = record => {
      return {
        onClick: () => {
          handleDetail(record.suratId)
        },
      }
    }

    onMounted(() => {
      fetchData()
    })

    // Initial fetch when route/query params change could be added here if needed

    return {
      loading,
      suratList,
      filteredList,
      searchText,
      columns,
      pagination,
      formatDate,
      formatDateTime,
      getStatusColor,
      handleTableChange,
      handleSearch,
      handleDetail,
      customRow,
    }
  },
})
</script>

<style lang="scss" scoped>
.ttd-digital-list {
  padding: 24px;
  background: #fcfcfc;
  min-height: calc(100vh - 64px);

  @media (max-width: 768px) {
    padding: 16px;
  }

  @media (max-width: 576px) {
    padding: 12px;
  }
}
.verifikasi-table {
  margin-bottom: 16px;

  :deep(.ant-table) {
    font-size: 14px;
  }

  :deep(.ant-table-thead > tr > th) {
    background: #f2f4f8 !important;
    font-weight: 600;
    color: #262626;
  }

  :deep(.ant-table-tbody > tr) {
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover > td {
      background-color: #e4e9f0 !important;
    }

    > td {
      background: white !important;
    }
  }

  :deep(.ant-table-tbody > tr > td) {
    border-right: none !important;
  }

  :deep(.ant-table-thead > tr > th) {
    border-right: none !important;
  }
}

.page-header {
  margin-bottom: 24px;

  @media (max-width: 768px) {
    margin-bottom: 16px;
  }

  .page-title {
    font-size: 24px;
    font-weight: 600;
    color: #262626;
    margin: 0;

    @media (max-width: 576px) {
      font-size: 20px;
    }
  }
}

.actions-bar {
  margin-bottom: 24px;

  @media (max-width: 768px) {
    margin-bottom: 16px;
  }

  :deep(.ant-input::placeholder) {
    color: #999999 !important;
    opacity: 1 !important;
    font-size: 14px;
  }
}

:deep(.ant-table) {
  background: white;
  border-radius: 26px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);

  .ant-table-thead > tr > th {
    background: #fafafa;
    font-weight: 600;
    color: #262626;
  }

  .ant-table-tbody > tr {
    cursor: pointer;

    &:hover > td {
      background: #fafafa;
    }
  }

  .surat-link {
    color: #262626;

    &:hover {
      color: #e01f48;
      text-decoration: underline;
    }
  }
}

.pagination-container {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}

::v-deep(.ant-tag) {
  font-weight: 700;
  border-radius: 50px;
}
</style>
